import domReady from '@wordpress/dom-ready';

const queryPaginationLoadStart = new Event( 'queryPaginationLoadStart' );
const queryPaginationLoadEnd = new Event( 'queryPaginationLoadEnd' );

const intersectionObserver = new window.IntersectionObserver(
	( entries ) => {
		entries.forEach( ( entry ) => {
			if ( entry.isIntersecting ) {
				fetchPosts( entry.target );
			}
		} );
	},
	{
		threshold: 0.5,
		rootMargin: '100px',
	}
);

/**
 * Load page from server, extract and append new posts to button's query block.
 *
 * @param {*} target
 */
const fetchPosts = ( target ) => {
	const button = target?.closest( '.rlt-load-more-button' );

	if ( ! button ) {
		return;
	}

	const url = button.href;
	const container = button
		.closest( '.wp-block-query' )
		?.querySelector( '.wp-block-post-template' );

	// Return early if button is still loading or required data not found.
	if ( button.classList.contains( 'loading' ) || ! container || ! url ) {
		return;
	}

	const fetchUrl = new URL( url, window.location.origin );

	// Not allowed to fetch from other origin.
	if ( fetchUrl.origin !== window.location.origin ) {
		return;
	}

	// Set loading text and classes.
	button.classList.add( 'loading' );

	// Dispatch event.
	document.dispatchEvent( queryPaginationLoadStart );

	// Load posts via fetch from the button URL.
	fetch( url, {
		method: 'GET',
		headers: {
			'Content-Type': 'text/html',
		},
	} )
		.then( function ( response ) {
			if ( response.ok ) {
				return response.text();
			}
			throw new Error( 'Network response was not ok.' );
		} )
		.then( function ( data ) {
			// Create temporary container to load fetched HTML.
			const tempContainer = document.createElement( 'div' );
			tempContainer.innerHTML = data;

			// Get loop scope from container.
			const containerLoopScope = container.dataset.rltQplmLoopScope || '';

			// Find container in fetched HTML matching container's loop scope.
			const posts = tempContainer.querySelector(
				`.wp-block-post-template[data-rlt-qplm-loop-scope="${ containerLoopScope }"]`
			);

			// Append the posts.
			if ( posts ) {
				container.insertAdjacentHTML( 'beforeend', posts.innerHTML );
			}

			const queryNextPage = +button.dataset.queryNextPage;
			const queryMaxPage = +button.dataset.queryMaxPage;

			// Update URL.
			if ( button.dataset.updateUrl ) {
				const newUrl = new URL( window.location.href );

				newUrl.searchParams.set(
					button.dataset.queryUrl,
					queryNextPage
				);
				window.history.pushState( {}, '', newUrl );
			}

			// Remove button if no more posts available.
			if ( queryNextPage >= queryMaxPage ) {
				if (
					button.classList.contains( 'rlt-load-more-infinite-scroll' )
				) {
					intersectionObserver.unobserve( button );
				}

				button.remove();

				return;
			}

			// Update button attributes.
			if ( queryNextPage < queryMaxPage ) {
				button.dataset.queryNextPage = queryNextPage + 1;
				button.href =
					'?' +
					button.dataset.queryUrl +
					'=' +
					button.dataset.queryNextPage;
			}
		} )
		.catch( ( error ) => {
			// eslint-disable-next-line no-console
			console.error( 'Fetch error:', error );
		} )
		// Cleanup.
		.finally( () => {
			// Reset loading text and classes.
			button.classList.remove( 'loading' );

			// Dispatch event.
			document.dispatchEvent( queryPaginationLoadEnd );

			if ( button.classList.contains( 'wp-load-more-infinite-scroll' ) ) {
				const bcr = button.getBoundingClientRect();

				// Fix not triggering the callback if the button is still visible.
				// If button is visible - toggle observing to ensure the
				// intersection observer triggers the callback again.
				if ( bcr.bottom > 0 && bcr.top < window.innerHeight ) {
					intersectionObserver.unobserve( button );
					intersectionObserver.observe( button );
				}
			}
		} );
};

/**
 * Setup buttons and add listeners when ready.
 */
domReady( () => {
	'use strict';

	// Load more buttons.
	// Prepare buttons and add listeners.
	document
		.querySelectorAll(
			'.rlt-load-more-button:not(.rlt-load-more-infinite-scroll)'
		)
		.forEach( function ( button ) {
			button.addEventListener( 'click', function ( e ) {
				e.preventDefault();

				fetchPosts( e.target );
			} );
		} );

	// Infinite scroll.
	document
		.querySelectorAll( '.rlt-load-more-infinite-scroll' )
		.forEach( function ( button ) {
			intersectionObserver.observe( button );
		} );
} );
